// frontend-participant/src/InterruptHandler.js
import React, { useState, useEffect } from 'react';

const API_ORIGIN = process.env.NODE_ENV === 'development' 
  ? "http://localhost:5000" 
  : "";

const InterruptHandler = ({ room, socket, botStatus }) => {
  const [interruptState, setInterruptState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState({});

  // Poll for interrupts when debug mode is active
  useEffect(() => {
    if (!room || !botStatus?.debug_mode || !botStatus?.active) {
      setInterruptState(null);
      setShowEditor(false);
      setEditedData(null);
      return;
    }

    const checkInterrupt = async () => {
      // Don't poll if we're actively editing - user is working on modifications
      if (showEditor) {
        return;
      }
      
      try {
        const response = await fetch(`${API_ORIGIN}/api/bot/${room}/interrupt`);
        const data = await response.json();
        
        if (data.has_interrupt) {
          // Only update if this is a new interrupt (not currently editing the same one)
          setInterruptState(prevState => {
            // If we're already showing this interrupt, don't reset editor state
            if (prevState && 
                prevState.node === data.interrupt.node && 
                JSON.stringify(prevState) === JSON.stringify(data.interrupt)) {
              return prevState; // Keep existing state, don't reset editor
            }
            // New interrupt - reset editor
            setEditedData(null);
            setShowEditor(false);
            return data.interrupt;
          });
        } else {
          // No interrupt - clear everything
          setInterruptState(null);
          setEditedData(null);
          setShowEditor(false);
        }
      } catch (error) {
        console.error('Error checking interrupt:', error);
      }
    };

    // Check immediately (unless already editing)
    if (!showEditor) {
      checkInterrupt();
    }

    // Then poll every 2 seconds while debug mode is active (will skip if editing)
    const interval = setInterval(checkInterrupt, 2000);

    return () => clearInterval(interval);
  }, [room, botStatus?.debug_mode, botStatus?.active, showEditor]);

  // Listen for bot status changes to catch interrupts
  useEffect(() => {
    if (!socket) return;

    const handleBotStatusChange = (data) => {
      if (data.room === room && data.has_interrupt) {
        setInterruptState(data.interrupt || null);
      } else if (data.room === room && !data.has_interrupt) {
        setInterruptState(null);
      }
    };

    socket.on('bot_status_changed', handleBotStatusChange);

    return () => {
      socket.off('bot_status_changed', handleBotStatusChange);
    };
  }, [socket, room]);

  const handleResume = async (modified = false) => {
    setLoading(true);
    try {
      const body = modified && editedData ? { modified_data: editedData } : {};
      
      const response = await fetch(`${API_ORIGIN}/api/bot/${room}/interrupt/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setInterruptState(null);
        setEditedData(null);
        setShowEditor(false);
      } else {
        alert(`Failed to resume: ${data.message}`);
      }
    } catch (error) {
      console.error('Error resuming from interrupt:', error);
      alert('Failed to resume from interrupt: Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (!interruptState) return;
    
    // Initialize edited data based on interrupt type
    if (interruptState.node === 'ACTIVE_AGENT') {
      setEditedData(JSON.parse(JSON.stringify(interruptState.tool_calls || [])));
    } else if (interruptState.node === 'JOIN_PASSIVES') {
      setEditedData(JSON.parse(JSON.stringify(interruptState.passive_tool_calls || {})));
    }
    
    setShowEditor(true);
  };

  const togglePrompt = (promptKey) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [promptKey]: !prev[promptKey]
    }));
  };

  const renderPromptSection = (promptKey, prompt, title = "Agent Prompt") => {
    if (!prompt) return null;
    
    const isExpanded = expandedPrompts[promptKey];
    
    return (
      <div className="interrupt-prompt-section">
        <button 
          className="interrupt-prompt-toggle"
          onClick={() => togglePrompt(promptKey)}
        >
          <span className="interrupt-prompt-toggle-icon">
            {isExpanded ? '▼' : '▶'}
          </span>
          {title}
        </button>
        {isExpanded && (
          <pre className="interrupt-prompt-content">
            {prompt}
          </pre>
        )}
      </div>
    );
  };

  // Available tools for active agent
  const ACTIVE_TOOLS = [
    { name: 'submit_current_field', label: 'Submit Current Field' },
    { name: 'ask_question', label: 'Ask Question' },
    { name: 'mental_model_for_instances', label: 'Mental Model (Repeat Manager)' }
  ];

  // Available tools for passive agents
  const PASSIVE_TOOLS = [
    { name: 'submit_value', label: 'Submit Value' },
    { name: 'append_to_scratchpad', label: 'Append to Scratchpad' },
    { name: 'no_op', label: 'No Operation' },
    { name: 'mental_model_for_instances', label: 'Mental Model (Repeat Manager)' }
  ];

  const getDefaultArgs = (toolName) => {
    switch (toolName) {
      case 'no_op':
        return {};
      case 'submit_current_field':
        return { value: '' };
      case 'ask_question':
        return { question: '' };
      case 'submit_value':
        return { value: '', reasoning: '' };
      case 'append_to_scratchpad':
        return { note: '' };
      case 'mental_model_for_instances':
        return {
          num_instances: 1,
          instances_dict: {},
          notes: ''
        };
      default:
        return {};
    }
  };

  const updateToolCall = (index, field, value) => {
    if (!editedData || !Array.isArray(editedData)) return;
    
    const newData = [...editedData];
    
    if (field === 'name') {
      // When tool changes, reset args to default for that tool
      newData[index].name = value;
      newData[index].args = getDefaultArgs(value);
    } else if (field === 'args') {
      try {
        newData[index].args = JSON.parse(value);
      } catch (e) {
        // Invalid JSON, don't update
        return;
      }
    } else {
      newData[index][field] = value;
    }
    setEditedData(newData);
  };

  const updatePassiveToolCall = (qId, field, value) => {
    if (!editedData || typeof editedData !== 'object') return;
    
    const newData = { ...editedData };
    if (!newData[qId]) return;
    
    if (field === 'tool') {
      // When tool changes, reset args to default for that tool
      newData[qId].tool = value;
      newData[qId].args = getDefaultArgs(value);
    } else if (field === 'args') {
      try {
        newData[qId].args = JSON.parse(value);
      } catch (e) {
        return;
      }
    } else {
      newData[qId][field] = value;
    }
    setEditedData(newData);
  };

  const renderArgsInput = (toolName, args, onChange) => {
    const argsJson = JSON.stringify(args || {}, null, 2);

    if (toolName === 'no_op') {
      return (
        <div style={{ 
          padding: '10px', 
          background: '#f8f9fa', 
          borderRadius: '6px',
          fontStyle: 'italic',
          color: '#6c757d'
        }}>
          No arguments required
        </div>
      );
    }

    if (toolName === 'submit_current_field') {
      // Simple text input for value only
      return (
        <input
          type="text"
          value={args?.value || ''}
          onChange={(e) => onChange(JSON.stringify({ value: e.target.value }))}
          className="interrupt-input"
          placeholder="Enter value..."
        />
      );
    }

    if (toolName === 'ask_question') {
      // Simple text input for question
      return (
        <input
          type="text"
          value={args?.question || ''}
          onChange={(e) => onChange(JSON.stringify({ question: e.target.value }))}
          className="interrupt-input"
          placeholder="Enter your question..."
        />
      );
    }

    if (toolName === 'append_to_scratchpad') {
      // Simple text input for note
      return (
        <input
          type="text"
          value={args?.note || ''}
          onChange={(e) => onChange(JSON.stringify({ note: e.target.value }))}
          className="interrupt-input"
          placeholder="Enter your note..."
        />
      );
    }

    if (toolName === 'submit_value') {
      // Two fields: value and reasoning
      return (
        <div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '0.9em' }}>
              Value:
            </label>
            <input
              type="text"
              value={args?.value || ''}
              onChange={(e) => onChange(JSON.stringify({ 
                value: e.target.value, 
                reasoning: args?.reasoning || '' 
              }))}
              className="interrupt-input"
              placeholder="Enter value..."
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '0.9em' }}>
              Reasoning:
            </label>
            <textarea
              value={args?.reasoning || ''}
              onChange={(e) => onChange(JSON.stringify({ 
                value: args?.value || '', 
                reasoning: e.target.value 
              }))}
              className="interrupt-textarea"
              style={{ minHeight: '80px' }}
              placeholder="Enter reasoning for this value..."
            />
          </div>
        </div>
      );
    }

    if (toolName === 'mental_model_for_instances') {
      // Structured input for repeat instances
      return (
        <textarea
          value={argsJson}
          onChange={(e) => onChange(e.target.value)}
          className="interrupt-textarea"
          placeholder={'{\n  "num_instances": 1,\n  "instances_dict": {},\n  "notes": ""\n}'}
        />
      );
    }

    // Fallback to JSON editor
    return (
      <textarea
        value={argsJson}
        onChange={(e) => onChange(e.target.value)}
        className="interrupt-textarea"
      />
    );
  };

  if (!interruptState) {
    return null; // No interrupt, don't show anything
  }

  return (
    <div className="interrupt-handler">
      <h3 className="interrupt-title">
        ⚠️ Review Required: {interruptState.message}
      </h3>
      
      <div className="interrupt-info">
        <strong>Step:</strong> {interruptState.node}
        {interruptState.q_id && (
          <div><strong>Question ID:</strong> {interruptState.q_id}</div>
        )}
      </div>

      {/* Active Agent Tool Calls */}
      {interruptState.node === 'ACTIVE_AGENT' && interruptState.tool_calls && (
        <div>
          {/* Show prompt for active agent */}
          {renderPromptSection('active_agent', interruptState.prompt, `Agent Prompt for ${interruptState.q_id}`)}
          
          <h4 className="interrupt-section-title">Proposed Actions:</h4>
          {!showEditor ? (
            <pre className="interrupt-preview">
              {JSON.stringify(interruptState.tool_calls, null, 2)}
            </pre>
          ) : (
            <div>
              {editedData && editedData.map((call, idx) => (
                <div key={idx} className="interrupt-tool-card">
                  <div className="interrupt-field">
                    <label className="interrupt-label">
                      Tool:
                    </label>
                    <select
                      value={call.name || ''}
                      onChange={(e) => updateToolCall(idx, 'name', e.target.value)}
                      className="interrupt-input"
                    >
                      {ACTIVE_TOOLS.map(tool => (
                        <option key={tool.name} value={tool.name}>
                          {tool.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="interrupt-field">
                    <label className="interrupt-label">
                      Arguments:
                    </label>
                    {renderArgsInput(
                      call.name,
                      call.args,
                      (value) => updateToolCall(idx, 'args', value)
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Passive Tool Calls */}
      {interruptState.node === 'JOIN_PASSIVES' && interruptState.passive_tool_calls && (
        <div>
          <h4 className="interrupt-section-title">Proposed Field Updates:</h4>
          {!showEditor ? (
            <div>
              {Object.entries(interruptState.passive_tool_calls).map(([qId, call]) => (
                <div key={qId} style={{ marginBottom: '16px' }}>
                  <h5 className="interrupt-card-title">Question ID: {qId}</h5>
                  {/* Show prompt for this passive agent */}
                  {call.prompt && renderPromptSection(`passive_preview_${qId}`, call.prompt, `Agent Prompt for ${qId}`)}
                  <pre className="interrupt-preview">
                    {JSON.stringify({ tool: call.tool, args: call.args }, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          ) : (
            <div>
              {editedData && Object.entries(editedData).map(([qId, call]) => (
                <div key={qId} className="interrupt-tool-card">
                  <h5 className="interrupt-card-title">Question ID: {qId}</h5>
                  {/* Show prompt for this passive agent */}
                  {call.prompt && renderPromptSection(`passive_${qId}`, call.prompt, `Agent Prompt for ${qId}`)}
                  <div className="interrupt-field">
                    <label className="interrupt-label">
                      Tool:
                    </label>
                    <select
                      value={call.tool || ''}
                      onChange={(e) => updatePassiveToolCall(qId, 'tool', e.target.value)}
                      className="interrupt-input"
                    >
                      {PASSIVE_TOOLS.map(tool => (
                        <option key={tool.name} value={tool.name}>
                          {tool.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="interrupt-field">
                    <label className="interrupt-label">
                      Arguments:
                    </label>
                    {renderArgsInput(
                      call.tool,
                      call.args,
                      (value) => updatePassiveToolCall(qId, 'args', value)
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="interrupt-actions">
        {!showEditor ? (
          <>
            <button
              onClick={() => handleResume(false)}
              disabled={loading}
              className="interrupt-btn interrupt-btn-continue"
            >
              {loading ? 'Continuing...' : '✓ Continue (Accept)'}
            </button>
            <button
              onClick={handleEdit}
              disabled={loading}
              className="interrupt-btn interrupt-btn-edit"
            >
              ✏️ Edit Actions
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => handleResume(true)}
              disabled={loading}
              className="interrupt-btn interrupt-btn-continue"
            >
              {loading ? 'Applying Changes...' : '✓ Continue with Changes'}
            </button>
            <button
              onClick={() => setShowEditor(false)}
              disabled={loading}
              className="interrupt-btn interrupt-btn-cancel"
            >
              Cancel Edit
            </button>
          </>
        )}
      </div>

      <div className="interrupt-help">
        <strong>Debug Mode:</strong> The bot is waiting for your approval. Review the proposed actions and either accept them or make changes before continuing.
      </div>
    </div>
  );
};

export default InterruptHandler;
